import { useMutation } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { toast } from 'sonner';

import { queryClient } from '~app/query-client';

import { baseAPI } from '~shared/api/base-api';
import { KEYS } from '~shared/api/query-keys';

import type { SendPushPayload, SendPushResult } from '../model/types';

export const sendPushAPI = async (payload: SendPushPayload) => {
  const response = await baseAPI.post<SendPushResult>(
    'mobile-inventor-push/send',
    payload,
  );
  return response.data;
};

function buildSendSummary(
  data: SendPushResult,
  scheduled: boolean,
): string {
  const verb = scheduled ? 'scheduled for' : 'sent to';
  return `Push ${verb} ${data.matchedEmails} parent(s) (${data.recipientCount} device(s))`;
}

export const useSendPush = () =>
  useMutation({
    mutationFn: sendPushAPI,
    onSuccess: (data, variables) => {
      // Prefix only — history queries include filter params and would miss
      // MOBILE_INVENTOR_PUSH_LOGS() which keys as [..., 'logs', {}].
      void queryClient.invalidateQueries({
        queryKey: [KEYS.MOBILE_INVENTOR_PUSH, 'logs'],
      });

      const summary = buildSendSummary(data, Boolean(variables.sendAt));
      const unmatchedCount = data.unmatchedEmails?.length ?? 0;
      const hasDegradation = Boolean(data.warning) || unmatchedCount > 0;

      if (hasDegradation) {
        const parts = [summary];
        if (unmatchedCount > 0) {
          parts.push(
            `${unmatchedCount} email(s) did not match an app user.`,
          );
        }
        if (data.warning?.trim()) {
          parts.push(data.warning.trim());
        }
        toast.warning(parts.join(' '));
        return;
      }

      toast.success(summary);
    },
    onError: (error: AxiosError<{ message?: string }>) => {
      toast.error(error.response?.data?.message ?? 'Failed to send push');
    },
  });
